import { type RosterWindowRules } from "@clipboard-health/contract-backend-main";
import { patch } from "@src/appV2/api/parsedApi";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { type RosterWindowSettings } from "../types";
import { invalidateRosterWindowSettingsQueries } from "./invalidateRosterWindowSettingsQueries";
import {
  rosterWindowSettingsResponseSchema,
  rosterWindowSettingsUpdateBodySchema,
} from "./rosterWindowSettingsSchemas";
import { rosterWindowSettingsUrl } from "./rosterWindowSettingsUrl";

// The window rules save as a complete group; the cancellation toggle can be
// flipped on its own. Modeled as a union (not `Partial<RosterWindowRules>`) so
// a partial rule subset or an empty update can't compile — mirroring the
// backend's all-or-none contract.
type UpdateRosterWindowSettingsAttributes =
  | (RosterWindowRules & { allowShiftCancellation?: boolean })
  | { allowShiftCancellation: boolean };

interface UpdateRosterWindowSettingsParams {
  workplaceId: string;
  attributes: UpdateRosterWindowSettingsAttributes;
}

const PRN_RESOURCE_TYPE = "roster-window-setting";

export function useUpdateRosterWindowSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateRosterWindowSettingsParams): Promise<RosterWindowSettings> => {
      const response = await patch({
        url: rosterWindowSettingsUrl(params.workplaceId),
        data: { data: { type: PRN_RESOURCE_TYPE, attributes: params.attributes } },
        bodySchema: rosterWindowSettingsUpdateBodySchema,
        responseSchema: rosterWindowSettingsResponseSchema,
      });
      const envelope = response.data;
      return {
        workplaceId: envelope.data.id,
        longLeadThresholdDays: envelope.data.attributes.longLeadThresholdDays,
        longLeadOpenDaysBeforeStart: envelope.data.attributes.longLeadOpenDaysBeforeStart,
        shortLeadOpenDaysAfterPosting: envelope.data.attributes.shortLeadOpenDaysAfterPosting,
        urgentThresholdDays: envelope.data.attributes.urgentThresholdDays,
        allowShiftCancellation: envelope.data.attributes.allowShiftCancellation ?? true,
        updatedAt: envelope.data.attributes.updatedAt,
      };
    },
    onSuccess: async () => {
      await invalidateRosterWindowSettingsQueries(queryClient);
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_ROSTER_WINDOW_SETTINGS_FAILURE,
    },
  });
}
